import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, ErrorState, Field, Input, Modal, ReadOnlyBanner, Select, Textarea } from '@bb/ui';
import { ApiError, type AdminApi, type ContentBlockDetails, type ContentBlockIssue, type ContentBlockKind, type ContentBlockUsageView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { TagPicker } from '../tags/TagPicker.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const issuesOf = (error: unknown): ContentBlockIssue[] => (error instanceof ApiError && Array.isArray(error.problem.issues) ? (error.problem.issues as ContentBlockIssue[]) : []);
const useApi = () => (useRouter().options.context as { api: AdminApi }).api;
const KIND_OPTIONS: Array<{ value: ContentBlockKind; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'html_subset', label: 'HTML' },
];
const BODY_HINT = 'Liquid with personalization, up to 50 KB. A block cannot include another block.';
const bytesOf = (text: string) => new TextEncoder().encode(text).length;

function IssueList({ error }: { error: unknown }) {
  const issues = issuesOf(error);
  return (
    <div role="alert" className="bb-field__error">
      <p>{error instanceof ApiError && error.code === 'content_block_version_conflict' ? 'Someone saved a newer version meanwhile: reload the block before saving.' : errorText(error)}</p>
      {issues.length > 0 && (
        <ul aria-label="Body issues">
          {issues.map((i) => (
            <li key={`${i.code}-${i.line ?? 0}`}>
              {i.message}
              {i.line ? ` (line ${i.line}, column ${i.column ?? 1})` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A new block: the key messages link it by (never changes), name, kind and the first body. */
export function NewContentBlockEditor({ environmentId }: { environmentId: string }) {
  const api = useApi();
  const navigate = useNavigate();
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ContentBlockKind>('text');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const create = useMutation({
    mutationFn: () => api.createContentBlock(environmentId, { key: key.trim(), name: name.trim(), kind, body, ...(description.trim() ? { description: description.trim() } : {}) }),
    onSuccess: (created) => void navigate({ to: '/env/$environmentId/content/blocks/$blockId', params: { environmentId, blockId: created.id } }),
  });
  const keyValid = /^[a-z][a-z0-9_]{0,63}$/.test(key.trim());
  return (
    <section className="template-editor">
      <p>
        <Link to="/env/$environmentId/content/blocks" params={{ environmentId }}>
          ← Content blocks
        </Link>
      </p>
      <h1>New content block</h1>
      <Field label="Key" hint="Lowercase letters, digits and _. Messages link the block by this key; it cannot change later." error={key && !keyValid ? 'Use lowercase letters, digits and _, starting with a letter.' : undefined}>
        <Input value={key} maxLength={64} onChange={(e) => setKey(e.target.value)} />
      </Field>
      <Field label="Block name">
        <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Kind">
        <Select options={KIND_OPTIONS} value={kind} onChange={(e) => setKind(e.target.value as ContentBlockKind)} />
      </Field>
      <Field label="Description">
        <Textarea value={description} rows={2} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="Body" hint={BODY_HINT}>
        <Textarea value={body} rows={8} spellCheck={false} onChange={(e) => setBody(e.target.value)} />
      </Field>
      {create.isError && <IssueList error={create.error} />}
      <Button variant="primary" disabled={!keyValid || !name.trim() || !body || create.isPending} onClick={() => create.mutate()}>
        Create block
      </Button>
    </section>
  );
}

/** Moves chosen running messages to one version of the block (the explicit update of their pinned version). */
export function PropagateDialog({ environmentId, block, open, onOpenChange, onDone }: { environmentId: string; block: ContentBlockDetails; open: boolean; onOpenChange: (open: boolean) => void; onDone: (text: string) => void }) {
  const api = useApi();
  const running = block.usage.filter((u): u is ContentBlockUsageView & { pinnedVersion: number } => u.pinnedVersion !== null);
  const [toVersion, setToVersion] = useState(block.currentVersion);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(running.filter((u) => u.pinnedVersion !== block.currentVersion).map((u) => u.objectId)));
  const propagate = useMutation({
    mutationFn: () =>
      api.propagateContentBlock(environmentId, block.id, {
        toVersion,
        targets: running.filter((u) => chosen.has(u.objectId)).map((u) => ({ type: u.objectType === 'canvas' ? ('canvas' as const) : ('campaign' as const), id: u.objectId })),
      }),
    onSuccess: (result) => {
      onDone(`${result.updated.length} running ${result.updated.length === 1 ? 'message now uses' : 'messages now use'} v${result.toVersion}.`);
      onOpenChange(false);
    },
  });
  const toggle = (id: string) =>
    setChosen((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Update running messages"
      description="Running messages keep the block version they were launched with. Choose which ones switch to another version; each change is recorded in the audit log."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={chosen.size === 0 || propagate.isPending} onClick={() => propagate.mutate()}>
            Update {chosen.size} {chosen.size === 1 ? 'message' : 'messages'}
          </Button>
        </>
      }
    >
      <Field label="Version">
        <Select options={block.versions.map((v) => ({ value: String(v.version), label: `v${v.version}${v.notes ? ` · ${v.notes}` : ''}` }))} value={String(toVersion)} onChange={(e) => setToVersion(Number(e.target.value))} />
      </Field>
      {running.length === 0 ? (
        <p className="hint">No running campaign or Canvas uses this block.</p>
      ) : (
        <ul className="plain-list" aria-label="Running messages">
          {running.map((u) => (
            <li key={u.objectId}>
              <label>
                <input type="checkbox" checked={chosen.has(u.objectId)} onChange={() => toggle(u.objectId)} /> {u.name} · {u.objectType === 'canvas' ? 'Canvas' : 'Campaign'} · uses v{u.pinnedVersion}
              </label>
            </li>
          ))}
        </ul>
      )}
      {propagate.isError && <IssueList error={propagate.error} />}
    </Modal>
  );
}

function EditorBody({ environmentId, block }: { environmentId: string; block: ContentBlockDetails }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has('content.edit') && !block.archivedAt;
  const [name, setName] = useState(block.name);
  const [description, setDescription] = useState(block.description ?? '');
  const [body, setBody] = useState(block.current.body);
  const [base, setBase] = useState(block.currentVersion);
  const [notes, setNotes] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [propagating, setPropagating] = useState(false);
  const queryKey = ['content-block', environmentId, block.id];
  const refresh = (next: ContentBlockDetails) => queryClient.setQueryData(queryKey, next);
  const saveDetails = useMutation({
    mutationFn: () => api.updateContentBlock(environmentId, block.id, { name: name.trim(), description: description.trim() || null }),
    onSuccess: (next) => {
      refresh(next);
      setNotice('Details saved.');
    },
  });
  const saveVersion = useMutation({
    mutationFn: () => api.addContentBlockVersion(environmentId, block.id, { body, baseVersion: base, ...(notes.trim() ? { notes: notes.trim() } : {}) }),
    onSuccess: (next) => {
      refresh(next);
      setBase(next.currentVersion);
      setNotes('');
      const pinned = next.usage.filter((u) => u.pinnedVersion !== null).length;
      setNotice(`Saved as version ${next.currentVersion}.${pinned ? ` ${pinned} running ${pinned === 1 ? 'message keeps' : 'messages keep'} the earlier version until you update them.` : ''}`);
    },
  });
  const loadVersion = useMutation({
    mutationFn: (version: number) => api.getContentBlockVersion(environmentId, block.id, version),
    onSuccess: (v) => {
      setBody(v.body);
      setNotice(`Version ${v.version} loaded into the editor: save it to make it the current version.`);
    },
  });
  const archive = useMutation({
    mutationFn: () => (block.archivedAt ? api.restoreContentBlock(environmentId, block.id) : api.archiveContentBlock(environmentId, block.id)),
    onSuccess: (next) => {
      refresh(next);
      void queryClient.invalidateQueries({ queryKey: ['content-blocks', environmentId] });
      const warnings = 'warnings' in next && Array.isArray(next.warnings) ? next.warnings : [];
      setNotice(next.archivedAt ? `Archived: hidden from the pickers.${warnings.length ? ` Drafts that link it: ${warnings.map((w) => w.name).join(', ')}.` : ''}` : 'Restored.');
    },
  });
  const archiveDependents = archive.error instanceof ApiError && Array.isArray(archive.error.problem.dependents) ? (archive.error.problem.dependents as Array<{ objectId: string; name: string }>) : [];
  const running = block.usage.filter((u) => u.pinnedVersion !== null);

  return (
    <section className="template-editor">
      <p>
        <Link to="/env/$environmentId/content/blocks" params={{ environmentId }}>
          ← Content blocks
        </Link>
      </p>
      <h1>{block.name}</h1>
      <p className="hint">
        Key <code>{block.key}</code> · current version v{block.currentVersion} · link it with <code>{block.tag}</code>
      </p>
      {!has('content.edit') && <ReadOnlyBanner>You can view content blocks but not change them.</ReadOnlyBanner>}
      {block.archivedAt && <p className="notice notice--warning">Archived {block.archivedAt.slice(0, 10)}: hidden from the pickers; publishing content that links it is refused.</p>}
      {notice && <p role="status">{notice}</p>}
      <fieldset className="stack-form">
        <legend>Details</legend>
        <Field label="Block name">
          <Input value={name} maxLength={200} disabled={!canEdit} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description">
          <Textarea value={description} rows={2} maxLength={1000} disabled={!canEdit} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        {canEdit && (
          <Button size="sm" variant="secondary" disabled={!name.trim() || saveDetails.isPending} onClick={() => saveDetails.mutate()}>
            Save details
          </Button>
        )}
        {saveDetails.isError && <IssueList error={saveDetails.error} />}
        <TagPicker environmentId={environmentId} objectType="content_block" objectId={block.id} canEdit={has('content.edit')} />
      </fieldset>
      <section aria-label="Body">
        <h2>Body</h2>
        <Field label="Body" hint={`${BODY_HINT} ${bytesOf(body)} bytes.`}>
          <Textarea value={body} rows={10} spellCheck={false} disabled={!canEdit} onChange={(e) => setBody(e.target.value)} />
        </Field>
        {canEdit && (
          <div className="stack-form">
            <Field label="Version notes">
              <Input value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
            </Field>
            <Button variant="primary" disabled={!body || saveVersion.isPending} onClick={() => saveVersion.mutate()}>
              Save as new version
            </Button>
          </div>
        )}
        {saveVersion.isError && <IssueList error={saveVersion.error} />}
      </section>
      <section aria-label="Versions">
        <h2>Versions</h2>
        <ol className="template-versions">
          {block.versions.map((v) => (
            <li key={v.version}>
              <strong>v{v.version}</strong> · {v.createdAt.slice(0, 10)}
              {v.createdBy?.email ? ` · ${v.createdBy.email}` : ''}
              {v.notes ? ` · ${v.notes}` : ''}
              {canEdit && v.version !== block.currentVersion && (
                <Button size="sm" variant="ghost" onClick={() => loadVersion.mutate(v.version)}>
                  Load v{v.version} into the editor
                </Button>
              )}
            </li>
          ))}
        </ol>
      </section>
      <section aria-label="Where used">
        <h2>Where used</h2>
        {block.usage.length === 0 ? (
          <p className="hint">No campaign or Canvas links this block yet.</p>
        ) : (
          <ul>
            {block.usage.map((u) => (
              <li key={`${u.objectType}:${u.objectId}`}>
                {u.url ? <a href={u.url}>{u.name}</a> : u.name} · {u.objectType === 'canvas' ? 'Canvas' : 'Campaign'}
                {u.status ? ` · ${u.status}` : ''} · {u.pinnedVersion === null ? 'draft: current version' : `running v${u.pinnedVersion}`}
              </li>
            ))}
          </ul>
        )}
        {canEdit && running.length > 0 && (
          <Button variant="secondary" onClick={() => setPropagating(true)}>
            Update running messages…
          </Button>
        )}
      </section>
      {has('content.edit') && (
        <div className="template-actions" role="group" aria-label="Content block actions">
          <Button variant={block.archivedAt ? 'secondary' : 'danger'} disabled={archive.isPending} onClick={() => archive.mutate()}>
            {block.archivedAt ? 'Restore' : 'Archive'}
          </Button>
        </div>
      )}
      {archive.isError && (
        <div role="alert" className="bb-field__error">
          <p>{errorText(archive.error)}</p>
          {archiveDependents.length > 0 && <p>Stop or finish these first: {archiveDependents.map((d) => d.name).join(', ')}.</p>}
        </div>
      )}
      {propagating && (
        <PropagateDialog
          environmentId={environmentId}
          block={block}
          open={propagating}
          onOpenChange={setPropagating}
          onDone={(text) => {
            setNotice(text);
            void queryClient.invalidateQueries({ queryKey });
          }}
        />
      )}
    </section>
  );
}

/** Content → Content blocks → one block (BB-57): details and tags, body versions, where-used with pinned versions, propagate, archive. */
export function ContentBlockEditor({ environmentId, blockId }: { environmentId: string; blockId: string }) {
  const api = useApi();
  const block = useQuery({ queryKey: ['content-block', environmentId, blockId], queryFn: () => api.getContentBlock(environmentId, blockId) });
  if (block.isPending) return <p aria-busy="true">Loading content block…</p>;
  if (block.isError) return <ErrorState title="The content block could not be loaded" message={errorText(block.error)} onRetry={() => void block.refetch()} />;
  return <EditorBody key={block.data.id} environmentId={environmentId} block={block.data} />;
}
